import { Button } from '@plato/ui-legacy/components/ui/button';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { base64ToBlob } from '@plato/ui-legacy/lib/utils/base64-to-blob';
import { DownloadIcon } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { OrderAttachment } from '../hooks/use-order-attachments.tsx';

type AttachmentDownloadButtonProps = {
    attachment: OrderAttachment;
};

export function AttachmentDownloadButton({ attachment }: AttachmentDownloadButtonProps) {
    const { t } = useTranslation('transaction-details');
    const { toast } = useToast();
    const [isDownloading, setIsDownloading] = useState(false);
    const utils = api.useUtils();
    const sendEvent = useAnalytics();

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDownloading(true);

        try {
            const result = await utils.platoUser.grid.getDetailQuoteOrder.getSalesDocument.fetch({
                documentId: attachment.documentId,
                documentType: attachment.documentType || '',
            });

            const pdfBlob = await base64ToBlob(result.pdfBase64, 'application/pdf');
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${attachment.documentName}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            sendEvent('transaction_document_downloaded', {
                document_id: attachment.documentId,
                document_type: attachment.documentType || '',
                document_name: attachment.documentName,
            });
        } catch (err) {
            console.error('Error downloading PDF:', err);
            toast({ icon: 'warning', title: t('attachments__error_download') });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Button
            variant="subtle"
            size="icon"
            onClick={handleDownload}
            aria-label={t('attachments__action_download')}
            isLoading={isDownloading}
            disabled={isDownloading}
            icon={DownloadIcon}
        />
    );
}
